import { apiSlice } from "../../api/apiSlice";

export const dashboardApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDashboardStats: builder.query({
      query: ({ period = "this-month", companyId = "" } = {}) => ({
        url: `/api/dashboard/stats`,
        params: { period, ...(companyId && { companyId }) },
      }),
    }),
    getDashboardOverview: builder.query({
      query: ({ period = "this-month", companyId = "" } = {}) => ({
        url: `/api/dashboard/overview`,
        params: { period, ...(companyId && { companyId }) },
      }),
    }),
    getCompaniesCount: builder.query({
      query: (period = "this-month") => ({
        url: `/api/dashboard/companies`,
        params: { period },
      }),
    }),
    getActivitiesCount: builder.query({
      query: (period = "this-month") => ({
        url: `/api/dashboard/activities`,
        params: { period },
      }),
    }),
    getFormsFilledCount: builder.query({
      query: (period = "this-month") => ({
        url: `/api/dashboard/forms-filled`,
        params: { period },
      }),
    }),
    getActivities: builder.query({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: `/api/activities/admin`,
        params: { page, limit, search },
      }),
      providesTags: ['Activities'],
    }),
    getActivityStats: builder.query({
      query: () => ({
        url: `/api/activities/stats`,
      }),
      providesTags: ['ActivityStats'],
    }),
    getActivity: builder.query({
      query: (id) => ({
        url: `/api/activities/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Activity', id }],
    }),
    createActivity: builder.mutation({
      query: (data) => ({
        url: `/api/activities`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ['Activities', 'ActivityStats'],
    }),
    updateActivity: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/activities/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Activity', id },
        'Activities',
        'ActivityStats',
        'PrestartForms',
        'Companies',
        'Company',
      ],
    }),
    updateActivityStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/api/activities/${id}`,
        method: "PATCH",
        body: { isActive: status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Activity', id },
        'Activities',
        'ActivityStats',
      ],
    }),
    deleteActivity: builder.mutation({
      query: (id) => ({
        url: `/api/activities/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ['Activities', 'ActivityStats'],
    }),
    getForms: builder.query({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: `/api/forms`,
        params: { page, limit, search },
      }),
    }),
    getFormStats: builder.query({
      query: () => ({
        url: `/api/forms/stats`,
      }),
    }),
    updateForm: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/forms/${id}`,
        method: "PATCH",
        body: { ...data },
      }),
    }),
    deleteForm: builder.mutation({
      query: (id) => ({
        url: `/api/forms/${id}`,
        method: "DELETE",
      }),
    }),
    getDocuments: builder.query({
      query: ({ page = 1, limit = 10, search = "", category = "" }) => ({
        url: `/api/documents/admin`,
        params: { page, limit, search, ...(category && { category }) },
      }),
      providesTags: ['Documents'],
    }),
    getDocumentStats: builder.query({
      query: () => ({
        url: `/api/documents/stats`,
      }),
      providesTags: ['DocumentStats'],
    }),
    getDocument: builder.query({
      query: (id) => ({
        url: `/api/documents/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
    uploadDocument: builder.mutation({
      query: (formData) => ({
        url: `/api/documents/upload`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ['Documents', 'DocumentStats'],
    }),
    updateDocument: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/documents/${id}`,
        method: "PATCH",
        body: { ...data },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Document', id },
        'Documents',
        'DocumentStats',
      ],
    }),
    deleteDocument: builder.mutation({
      query: (id) => ({
        url: `/api/documents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ['Documents', 'DocumentStats'],
    }),
    getCompanies: builder.query({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: `/api/companies`,
        params: { page, limit, search },
      }),
      providesTags: ['Companies'],
    }),
    getCompanyStats: builder.query({
      query: () => ({
        url: `/api/companies/stats`,
      }),
      providesTags: ['CompanyStats'],
    }),
    getCompany: builder.query({
      query: (id) => ({
        url: `/api/companies/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "Company", id }, "Companies"],
    }),
    createCompany: builder.mutation({
      query: (data) => ({
        url: `/api/companies`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ['Companies', 'CompanyStats'],
    }),
    updateCompany: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/companies/${id}`,
        method: "PATCH",
        body: { ...data },
      }),
      invalidatesTags: ['Companies', 'CompanyStats'],
    }),
    updateCompanyStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/api/companies/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ['Companies', 'CompanyStats'],
    }),
    deleteCompany: builder.mutation({
      query: (id) => ({
        url: `/api/companies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ['Companies', 'CompanyStats'],
    }),
    getContactsConfig: builder.query({
      query: () => ({
        url: `/api/contacts/config`,
      }),
    }),
    updateContactsConfig: builder.mutation({
      query: (data) => ({
        url: `/api/contacts/config`,
        method: "PATCH",
        body: data,
      }),
    }),
    // Emergency Config APIs
    getEmergencyConfig: builder.query({
      query: () => ({
        url: `/api/emergency-config`,
      }),
    }),
    updateCompanyEmergencyNumbers: builder.mutation({
      query: (data) => ({
        url: `/api/emergency-config/company`,
        method: "PUT",
        body: data,
      }),
    }),
    updateGlobalEmergencyNumber: builder.mutation({
      query: (data) => ({
        url: `/api/emergency-config/global`,
        method: "PUT",
        body: data,
      }),
    }),
    // Admin Form Submissions APIs
    getAllFormSubmissions: builder.query({
      query: ({
        page = 1,
        limit = 10,
        startDate = "",
        endDate = "",
        activityId = "",
        guideName = "",
        formType = "all",
      }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          formType,
        });
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (activityId) params.append("activityId", activityId);
        if (guideName) params.append("guideName", guideName);
        return {
          url: `/api/admin/form-submissions?${params.toString()}`,
        };
      },
    }),
    getFormSubmissionById: builder.query({
      query: ({ formType, id }) => ({
        url: `/api/admin/form-submissions/${formType}/${id}`,
      }),
    }),
    downloadFormSubmission: builder.mutation({
      query: ({ formType, id, timeZone }) => ({
        url: `/api/admin/form-submissions/${formType}/${id}/download`,
        method: "GET",
        params: timeZone ? { timeZone } : undefined,
        responseHandler: async (response) => {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            return response.json();
          }
          const blob = await response.blob();
          return {
            blob,
            contentType,
            filename: response.headers.get("content-disposition") || "",
          };
        },
      }),
    }),
    downloadFormSubmissionsBulk: builder.mutation({
      query: (body) => ({
        url: `/api/admin/form-submissions/download`,
        method: "POST",
        body,
        responseHandler: async (response) => {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            return response.json();
          }
          const blob = await response.blob();
          return {
            blob,
            contentType,
            filename: response.headers.get("content-disposition") || "",
          };
        },
      }),
    }),
    // Guides APIs
    getGuides: builder.query({
      query: (companyId) => {
        // For Admin, companyId is optional - backend will use their own company
        // For Super Admin, companyId can be provided to filter
        const url = companyId
          ? `/api/onboarding/guides/${companyId}`
          : `/api/onboarding/guides`;
        return { url };
      },
    }),
    createGuide: builder.mutation({
      query: (data) => ({
        url: `/api/onboarding/guide`,
        method: "POST",
        body: data,
      }),
    }),
    createAdmin: builder.mutation({
      query: (data) => ({
        url: `/api/onboarding/admin`,
        method: "POST",
        body: data,
      }),
    }),
    updateGuideStatus: builder.mutation({
      query: ({ userId, isActive }) => ({
        url: `/api/onboarding/user/${userId}/status`,
        method: "PATCH",
        body: { isActive },
      }),
    }),
    // Prestart Forms APIs
    getPrestartForms: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        activityId = "",
        isActive = "",
      } = {}) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (search) params.append("search", search);
        if (activityId) params.append("activityId", activityId);
        if (isActive) params.append("isActive", isActive);
        return {
          url: `/api/prestart-forms?${params.toString()}`,
        };
      },
      providesTags: ["PrestartForms"],
    }),
    getPrestartFormById: builder.query({
      query: (arg) => {
        const id = typeof arg === "object" && arg !== null ? arg.id : arg;
        const version = typeof arg === "object" && arg !== null ? arg.version : undefined;
        const params = new URLSearchParams();
        if (version) params.append("version", String(version));
        const qs = params.toString();
        return {
          url: `/api/prestart-forms/${id}${qs ? `?${qs}` : ""}`,
        };
      },
      providesTags: (result, error, arg) => {
        const id = typeof arg === "object" && arg !== null ? arg.id : arg;
        return [{ type: "PrestartForm", id }];
      },
    }),
    getPrestartFormVersions: builder.query({
      query: (id) => ({
        url: `/api/prestart-forms/${id}/versions`,
      }),
      providesTags: (result, error, id) => [{ type: "PrestartForm", id }, "PrestartForms"],
    }),
    createPrestartForm: builder.mutation({
      query: (data) => ({
        url: `/api/prestart-forms`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        const id = result?.data?.prestartForm?._id || result?.data?.prestartForm?.id;
        return id ? [{ type: "PrestartForm", id }, "PrestartForms"] : ["PrestartForms"];
      },
    }),
    updatePrestartForm: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/prestart-forms/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrestartForm", id }, "PrestartForms"],
    }),
    publishPrestartForm: builder.mutation({
      query: ({ id, version, companyIds }) => ({
        url: `/api/prestart-forms/${id}/publish`,
        method: "POST",
        body: { version, companyIds },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrestartForm", id }, "PrestartForms", "Companies", "Company"],
    }),
    assignPrestartFormCompanies: builder.mutation({
      query: ({ id, companyIds, version }) => ({
        url: `/api/prestart-forms/${id}/companies`,
        method: "POST",
        body: { companyIds, ...(version ? { version } : {}) },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrestartForm", id }, "PrestartForms", "Companies", "Company"],
    }),
    unassignPrestartFormCompanies: builder.mutation({
      query: ({ id, companyIds }) => ({
        url: `/api/prestart-forms/${id}/companies`,
        method: "DELETE",
        body: { companyIds },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrestartForm", id }, "PrestartForms", "Companies", "Company"],
    }),
    restorePrestartFormVersion: builder.mutation({
      query: ({ id, version }) => ({
        url: `/api/prestart-forms/${id}/versions/${version}/restore`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrestartForm", id }, "PrestartForms"],
    }),
    deletePrestartFormVersion: builder.mutation({
      query: ({ id, version }) => ({
        url: `/api/prestart-forms/${id}/versions/${version}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrestartForm", id }, "PrestartForms", "Companies", "Company"],
    }),
  }),
});

export const {
  useGetDashboardStatsQuery,
  useGetDashboardOverviewQuery,
  useGetCompaniesCountQuery,
  useGetActivitiesCountQuery,
  useGetFormsFilledCountQuery,
  useGetActivitiesQuery,
  useGetActivityStatsQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useUpdateActivityStatusMutation,
  useDeleteActivityMutation,
  useGetFormsQuery,
  useGetFormStatsQuery,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useGetDocumentsQuery,
  useGetDocumentStatsQuery,
  useGetDocumentQuery,
  useUploadDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useGetCompaniesQuery,
  useGetCompanyStatsQuery,
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useUpdateCompanyStatusMutation,
  useDeleteCompanyMutation,
  useGetContactsConfigQuery,
  useUpdateContactsConfigMutation,
  useGetEmergencyConfigQuery,
  useUpdateCompanyEmergencyNumbersMutation,
  useUpdateGlobalEmergencyNumberMutation,
  useGetAllFormSubmissionsQuery,
  useGetFormSubmissionByIdQuery,
  useDownloadFormSubmissionMutation,
  useDownloadFormSubmissionsBulkMutation,
  useGetGuidesQuery,
  useCreateGuideMutation,
  useUpdateGuideStatusMutation,
  useCreateAdminMutation,
  useGetPrestartFormsQuery,
  useGetPrestartFormByIdQuery,
  useGetPrestartFormVersionsQuery,
  useCreatePrestartFormMutation,
  useUpdatePrestartFormMutation,
  usePublishPrestartFormMutation,
  useAssignPrestartFormCompaniesMutation,
  useUnassignPrestartFormCompaniesMutation,
  useRestorePrestartFormVersionMutation,
  useDeletePrestartFormVersionMutation,
} = dashboardApiSlice;
